import {
  USER_FETCH_STORE_ERROR,
  UNREAD_MESSAGES_FETCH_STORE_ERROR,
} from '@/entities/user/lib/userMessages';
import type { IUser } from '@/entities/user/model/types/user';
import {
  ACCESS_TOKEN_COOKIE_NAME,
  LAWYER_USER_ID_COOKIE_NAME,
  COOKIE_MAX_AGE,
} from '@/shared/constants/cookiesConstants';
import { unwrapData } from '@/shared/utils/unwrapApiResponse';
import {
  ROLE_TYPE_USER,
  ROLE_TYPE_LAWYER,
  ROLE_TYPE_COMPANY,
} from '@/shared/constants/roleTypeConstants';
import { logError, normalizeError } from '@/shared/lib/errors';

export const useUserStore = defineStore('user', () => {
  const { $userService } = useNuxtApp();

  const user: Ref<IUser | null> = ref(null);
  const isAuth: Ref<boolean> = ref(false);
  const unreadMessagesNumber = ref(0);

  const isRoleTypeUser: ComputedRef<boolean> = computed(
    () => Number(user.value?.role_type) === ROLE_TYPE_USER,
  );
  const isRoleTypeLawyer: ComputedRef<boolean> = computed(
    () => Number(user.value?.role_type) === ROLE_TYPE_LAWYER,
  );
  const isRoleTypeCompany: ComputedRef<boolean> = computed(
    () => Number(user.value?.role_type) === ROLE_TYPE_COMPANY,
  );

  const getUser = async (token?: string): Promise<void> => {
    try {
      const profile = unwrapData(await $userService.getUser(token));

      if (profile?.id) {
        user.value = profile;
        isAuth.value = true;
      }
    } catch (error) {
      logError(normalizeError(error), { action: USER_FETCH_STORE_ERROR });
    }
  };

  let unreadMessagesRequest: Promise<void> | null = null;

  const getUnreadMessagesNumber = (): Promise<void> => {
    if (unreadMessagesRequest) return unreadMessagesRequest;

    unreadMessagesRequest = (async () => {
      try {
        const data = unwrapData(await $userService.getMyUnreadMessagesNumber());
        if (data && isAuth.value) unreadMessagesNumber.value = data.count;
      } catch (error) {
        logError(normalizeError(error), { action: UNREAD_MESSAGES_FETCH_STORE_ERROR });
      } finally {
        unreadMessagesRequest = null;
      }
    })();

    return unreadMessagesRequest;
  };

  const startSession = async (token: string): Promise<void> => {
    useCookie(ACCESS_TOKEN_COOKIE_NAME, { path: '/' }).value = null;
    useCookie(ACCESS_TOKEN_COOKIE_NAME, { maxAge: COOKIE_MAX_AGE, path: '/' }).value =
      token;
    await getUser(token);
  };

  const setHasNotification = (value: boolean): void => {
    if (!user.value) return;
    user.value.hasNotification = value;
  };

  const logout = (): void => {
    useCookie(ACCESS_TOKEN_COOKIE_NAME, { path: '/' }).value = null;
    useCookie(LAWYER_USER_ID_COOKIE_NAME, { path: '/' }).value = null;
    isAuth.value = false;
    user.value = null;
    unreadMessagesNumber.value = 0;
  };

  return {
    user,
    isAuth,
    isRoleTypeUser,
    isRoleTypeLawyer,
    isRoleTypeCompany,
    unreadMessagesNumber,
    getUser,
    getUnreadMessagesNumber,
    startSession,
    setHasNotification,
    logout,
  };
});
